import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	X,
	CheckCircle2,
	MessageSquare,
	FileText,
	Send,
	ArrowUpRight,
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Button } from "../../components/ui/button";
import { useAttributesStore } from "../../store/Attributes/attributes.store";

interface ConnectionNextStepsCardProps {
	clientId: string;
	connectedEmail: string;
}

const CONNECT_NEXT_STEPS_DISMISSED_KEY = "connect_next_steps_dismissed";

export const ConnectionNextStepsCard: React.FC<
	ConnectionNextStepsCardProps
> = ({ clientId, connectedEmail }) => {
	const [dismissed, setDismissed] = useState(false);
	const [dontShowAgain, setDontShowAgain] = useState(false);
	const navigate = useNavigate();
	const setAttribute = useAttributesStore((state) => state.setAttribute);

	if (dismissed) return null;

	const handleDismiss = () => {
		if (dontShowAgain) {
			setAttribute(CONNECT_NEXT_STEPS_DISMISSED_KEY, 1, "profile");
		}
		setDismissed(true);
	};

	const steps = [
		{
			icon: FileText,
			text: "Dateien aus Aufträgen direkt teilen",
			linkLabel: "Zu den Aufträgen",
			path: `/client/${clientId}`,
			testId: "connection-next-steps-link-chats",
		},
		{
			icon: Send,
			text: "Monatsrechnungen direkt freigeben",
			linkLabel: "Zu den Monatsrechnungen",
			path: "/monthly-invoices",
			testId: "connection-next-steps-link-invoices",
		},
		{
			icon: MessageSquare,
			text: "Nachrichten austauschen",
			linkLabel: "Chat öffnen",
			path: `/client/${clientId}/general-chat`,
			testId: "connection-next-steps-link-chat",
		},
	];

	return (
		<Card className="py-0 gap-0" data-testid="connection-next-steps-card">
			<CardContent className="p-0">
				<div className="flex items-start justify-between px-5 pt-4 pb-3">
					<div className="flex items-start gap-3">
						<div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100">
							<CheckCircle2 className="h-4 w-4 text-emerald-600" />
						</div>
						<div>
							<h3 className="text-sm font-semibold leading-none mb-1.5">
								Verbindung hergestellt
							</h3>
							<p className="text-xs text-muted-foreground">
								Einladungs-E-Mail gesendet an{" "}
								<span className="font-medium text-foreground">
									{connectedEmail}
								</span>
							</p>
						</div>
					</div>
					<Button
						variant="ghost"
						size="icon"
						className="h-7 w-7 -mt-0.5 -mr-1 shrink-0"
						onClick={handleDismiss}
						aria-label="Hinweis schliessen"
						data-testid="connection-next-steps-dismiss"
					>
						<X className="h-3.5 w-3.5" />
					</Button>
				</div>

				<div className="border-t mx-5" />

				<div className="px-2 py-2">
					<p className="px-3 pt-2 pb-1 text-xs font-medium text-muted-foreground uppercase tracking-wide">
						Nächste Schritte
					</p>
					{steps.map((step) => (
						<button
							key={step.testId}
							onClick={() => navigate(step.path)}
							className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-accent transition-colors text-left group"
							aria-label={step.linkLabel}
							data-testid={step.testId}
						>
							<step.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
							<div className="flex-1 min-w-0">
								<span className="text-sm">{step.text}</span>
							</div>
							<ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
						</button>
					))}
					<div className="flex items-center gap-3 px-3 py-2.5">
						<ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground opacity-0" />
						<span className="text-sm text-muted-foreground">
							Ihr Kunde kann digitale Auftragsanfragen direkt an
							Sie senden
						</span>
					</div>
				</div>

				<div className="border-t mx-5" />

				<div className="flex items-center gap-2 px-5 py-3">
					<Checkbox
						id="dont-show-again"
						checked={dontShowAgain}
						onCheckedChange={(checked) =>
							setDontShowAgain(checked === true)
						}
						aria-label="Nicht erneut anzeigen"
						data-testid="connection-next-steps-dont-show-checkbox"
					/>
					<label
						htmlFor="dont-show-again"
						className="text-xs text-muted-foreground cursor-pointer select-none"
					>
						Nicht erneut anzeigen
					</label>
				</div>
			</CardContent>
		</Card>
	);
};
